import {useId} from 'react'
import {CircleIcon, CheckCircleFillIcon, StackIcon, XCircleFillIcon} from '@primer/octicons-react'
import CircleOcticon from '@github-ui/circle-octicon'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {NavList} from '@primer/react'

import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {HEADER_ICON_SIZE} from '../../constants'
import type {StackEntryPayload} from '../../page-data/payloads/merge-box'
import type {StackSectionStatusType} from '../../helpers/merge-box-status-calculator/stack-section-status'
import {PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'

export function StackSection({
  pullRequestId,
  status,
  entries,
}: {
  pullRequestId: string
  status: StackSectionStatusType
  entries: StackEntryPayload[]
}) {
  const stackSectionAriaId = useId()
  const [stackExpanded, setStackExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:stackExpanded`,
    status !== 'PASSED',
  )

  const previousEntries = entries.filter(e => e.position === 'BEFORE' && e.pull.state !== 'MERGED')
  const isFirst = previousEntries.length === 0

  let title
  let subtitle
  switch (status) {
    case 'FAILED':
      // TODO: Better wording!
      title = 'PR does not meet stack conditions'
      subtitle = 'This PR is not correctly stacked'
      break
    case 'BLOCKED':
      title = 'PRs earlier in the stack are not ready to merge'
      subtitle = `${previousEntries.length} stacked PR${
        previousEntries.length === 1 ? '' : 's'
      } will be merged with this one.`
      break
    case 'PASSED':
      if (isFirst) {
        title = 'First unmerged PR in the stack'
        subtitle = 'This is the first unmerged PR in the stack, it can be merged individually.'
      } else {
        title = 'PRs earlier in the stack are ready to merge'
        subtitle = `${previousEntries.length} stacked PR${
          previousEntries.length === 1 ? '' : 's'
        } will be merged with this one.`
      }
      break
    case 'UNKNOWN':
    default:
      title = 'Stack status unknown'
      subtitle = `${previousEntries.length} stacked PR${
        previousEntries.length === 1 ? '' : 's'
      } will be merged with this one.`
      break
  }

  return (
    <section
      aria-label="Stacked pull requests"
      aria-describedby={stackSectionAriaId}
      className="border-bottom color-border-subtle"
    >
      <MergeBoxSectionHeader
        headerId={stackSectionAriaId}
        title={title}
        subtitle={subtitle}
        icon={<StatusIcon status={status} />}
        expandableProps={{
          ariaLabel: stackExpanded ? 'Collapse stack details' : 'Expand stack details',
          isExpanded: stackExpanded,
          onToggle: () => {
            setStackExpanded(!stackExpanded)
          },
        }}
      />
      <MergeBoxExpandable isExpanded={stackExpanded}>
        <NavList className="tmp-m-3">
          {entries.map(e => (
            <NavList.Item key={e.pull.number} href={e.pull.url} aria-current={e.position === 'CURRENT'}>
              <EntryListItem entry={e} />
            </NavList.Item>
          ))}
        </NavList>
      </MergeBoxExpandable>
    </section>
  )
}

function EntryListItem({entry}: {entry: StackEntryPayload}) {
  if (!entry) {
    return
  }

  const hasErrors = entry.failedSubConditions && entry.failedSubConditions.length > 0

  // TODO: Resolve inconsistent representation of queued state
  const pullState = (entry.pull.state as string) === 'QUEUED' ? 'IN_MERGE_QUEUE' : entry.pull.state

  return (
    <>
      <EntryStatusIcon entry={entry} hasErrors={hasErrors} /> {PullRequestStateIcons[pullState]}{' '}
      <span className={entry.position === 'AFTER' ? 'fgColor-muted' : ''}>{entry.pull.title}</span>{' '}
      <span className="fgColor-muted">#{entry.pull.number}</span>
      {hasErrors && (
        <ul>
          {entry.failedSubConditions.map(cond => (
            <li key={cond.displayName}>
              <strong>{cond.displayName}</strong>: {cond.message}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function EntryStatusIcon({entry, hasErrors}: {entry: StackEntryPayload; hasErrors: boolean}) {
  if (entry.position === 'AFTER') {
    return <CircleIcon size={16} className="color-fg-muted" aria-label="Will not merge: higher up the stack" />
  } else if (entry.pull.state === 'MERGED') {
    return <CircleIcon size={16} className="color-fg-muted" aria-label="Already merged" />
  } else if (hasErrors) {
    return <XCircleFillIcon size={16} className="color-fg-danger" aria-label="Cannot merge" />
  } else {
    return <CheckCircleFillIcon size={16} className="color-fg-success" aria-label="Ready to merge" />
  }
}

function StatusIcon({status}: {status: StackSectionStatusType}) {
  let className
  switch (status) {
    case 'BLOCKED':
    case 'FAILED':
      className = 'bgColor-danger-emphasis'
      break
    case 'PASSED':
      className = 'bgColor-success-emphasis'
      break
    case 'UNKNOWN':
    default:
      className = 'bgColor-muted'
      break
  }

  return <CircleOcticon className={className} size={HEADER_ICON_SIZE} icon={() => <StackIcon size={16} />} />
}
